import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { t } from '../../i18n/i18n';
import CheckWithCircle from '../../assets/tests/CheckWithCircle';
import ClockIcon from '../../assets/tests/Clock';
import { getPresetColor } from '../../utils/presetColors';
import useTranslations from '../../hooks/useTranslations';
import { getTranslatedValue } from '../../utils/translationHelpers';
import './AdvancedSettingsModal.scss';

const AdvancedSettingsModal = ({
  open,
  onClose,
  testGroup,
  selectionMode = false,
  selectedPresetId: initialSelectedPresetId,
  onPresetSelect,
  onConfirm,
  companyId = 0,
}) => {
  const INITIAL_VISIBLE_CONFIGS = 4;

  const [localSelectedPresetId, setLocalSelectedPresetId] = useState(initialSelectedPresetId);
  const [expandedPresets, setExpandedPresets] = useState({});
  const { key: langKey } = useTranslations();

  useEffect(() => {
    if (open) {
      setLocalSelectedPresetId(initialSelectedPresetId);
      setExpandedPresets({});
    }
  }, [initialSelectedPresetId, open]);

  if (!testGroup?.advancedSetting?.available || !testGroup?.advancedSetting?.preSettings?.length) {
    return null;
  }

  // Ordenar configuraciones por cantidad de preguntas (menor a mayor)
  const sortedPresets = [...testGroup.advancedSetting.preSettings].sort((a, b) => {
    const configsA = a.difficultyConfig || [];
    const configsB = b.difficultyConfig || [];
    const totalA = configsA.reduce((sum, config) => sum + config.quantity, 0) || 0;
    const totalB = configsB.reduce((sum, config) => sum + config.quantity, 0) || 0;
    return totalA - totalB;
  });

  const handleCancel = (e) => {
    if (e) e.stopPropagation();
    onClose(localSelectedPresetId);
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const handlePresetClick = (presetId) => {
    if (selectionMode) {
      setLocalSelectedPresetId(presetId);
      if (onPresetSelect) {
        onPresetSelect(presetId);
      }
    }
  };

  const handleConfirm = () => {
    if (selectionMode && onConfirm) {
      onConfirm(localSelectedPresetId);
    }
  };

  const togglePresetExpansion = (presetId) => {
    setExpandedPresets((prev) => ({
      ...prev,
      [presetId]: !prev[presetId],
    }));
  };

  return (
    <Modal
      title={
        selectionMode
          ? t('TESTGROUP_ADVANCED_SELECT_CONFIG')
          : t('TESTGROUP_ADVANCED_CONFIGS_AVAILABLE')
      }
      open={open}
      onCancel={handleCancel}
      width={700}
      footer={
        selectionMode
          ? [
              <button
                type="button"
                key="cancel"
                className="btn btn-secondary mr-2"
                style={{ borderRadius: '4px' }}
                onClick={handleCancel}
              >
                {t('CANCEL')}
              </button>,
              <button
                type="button"
                key="confirm"
                className="btn btn-primary"
                style={{ borderRadius: '4px' }}
                onClick={handleConfirm}
                disabled={localSelectedPresetId === undefined || localSelectedPresetId === null}
              >
                {t('CONFIRM')}
              </button>,
            ]
          : [
              <button
                type="button"
                key="close"
                className="btn btn-secondary"
                style={{ borderRadius: '4px' }}
                onClick={handleCancel}
              >
                {t('CANCEL')}
              </button>,
            ]
      }
      getContainer={() => document.body}
      maskClosable={false}
      onClick={handleModalClick}
    >
      <div className="advanced-settings-container">
        {selectionMode && (
          <p className="mb-3">{t('TESTGROUP_ADVANCED_SELECT_CONFIG_DESCRIPTION')}</p>
        )}
        <div className="d-flex flex-column gap-3">
          {sortedPresets.map((preset, index) => {
            const configs = preset.difficultyConfig || [];
            const totalQuestions = configs.reduce((sum, config) => sum + config.quantity, 0) || 0;
            const presetDuration = preset.durationMinutes || preset.duration;
            const totalDuration =
              presetDuration ||
              configs.reduce((sum, config) => sum + config.quantity * config.duration, 0) ||
              0;

            // Get metrics for this preset if available
            let averageCompletionTime = null;

            // Check different sources for average completion time
            // 1. First check if we have preset-specific analysis in metrics
            if (testGroup?.metrics?.presetAnalysis && preset.id === initialSelectedPresetId) {
              averageCompletionTime = testGroup.metrics.presetAnalysis.averageCompletionTime;
            }
            // 2. Check if we have difficultyAnalyses array in metrics
            else if (
              testGroup?.metrics?.difficultyAnalyses &&
              Array.isArray(testGroup.metrics.difficultyAnalyses)
            ) {
              const analysis = testGroup.metrics.difficultyAnalyses.find(
                (a) => a.presetId === preset.id.toString()
              );
              if (analysis?.overallMetrics?.averageCompletionTime) {
                averageCompletionTime = analysis.overallMetrics.averageCompletionTime;
              }
            }
            // 3. Fall back to overall average if available
            else if (testGroup?.metrics?.averageCompletionTime) {
              averageCompletionTime = testGroup.metrics.averageCompletionTime;
            }
            // If no metrics are available, we'll use the configured duration

            const colorConfig = getPresetColor(testGroup, preset.id);
            const isSelected = selectionMode && localSelectedPresetId === preset.id;
            const isExpanded = expandedPresets[preset.id] || false;
            const visibleConfigs = isExpanded ? configs : configs.slice(0, INITIAL_VISIBLE_CONFIGS);
            const hasMoreConfigs = configs.length > INITIAL_VISIBLE_CONFIGS;

            return (
              <div
                key={preset.id}
                className={`preset-item p-3 rounded border ${
                  selectionMode ? 'cursor-pointer' : ''
                } ${isSelected ? 'selected' : ''}`}
                style={
                  colorConfig
                    ? {
                        backgroundColor: colorConfig.bg,
                        border: `2px solid ${colorConfig.border}`,
                        color: colorConfig.text,
                      }
                    : {}
                }
                onClick={() => handlePresetClick(preset.id)}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="font-size-16 font-weight-600 m-0 d-flex align-items-center">
                    <span className="config-order-badge">{index + 1}</span>
                    {getTranslatedValue(preset.displayName, langKey) ||
                      preset.displayName ||
                      t('TESTGROUP_ADVANCED_CONFIG_N').replace('{{n}}', preset.id)}
                  </h4>
                  <div className="d-flex gap-3 text-sm">
                    <span className="d-flex align-items-center stat-badge questions-badge">
                      <CheckWithCircle size="15" className="mr-1" />
                      <strong>{totalQuestions}</strong>&nbsp;{t('TEST_ITEM_QUESTION')}
                      {totalQuestions !== 1 ? 's' : ''}
                    </span>
                    <span className="d-flex align-items-center stat-badge duration-badge">
                      <ClockIcon width="8" className="mr-1" />
                      <strong>
                        {averageCompletionTime
                          ? Math.round(averageCompletionTime / 60)
                          : totalDuration}
                      </strong>
                      &nbsp;{t('MODAL_SUBMISSION_MINUTES')}
                      {averageCompletionTime && (
                        <span className="ml-1 text-xs text-muted">({t('AVG')})</span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="difficulty-breakdown">
                  {visibleConfigs.map((config, idx) => (
                    <div key={`config-${idx}`} className="config-detail-item text-xs mb-1">
                      <span className="font-weight-500">
                        {getTranslatedValue(config.displayName, langKey) || config.displayName}:
                      </span>{' '}
                      {config.quantity} {t('TEST_ITEM_QUESTION')}
                      {config.quantity !== 1 ? 's' : ''}
                      {config.theme && <span className="ml-2">({config.theme})</span>}
                      {config.subSkillIds && config.subSkillIds.length > 0 && (
                        <span className="ml-2 text-muted">
                          ({config.subSkillIds.length} {t('SUBSKILLS').toLowerCase()})
                        </span>
                      )}
                    </div>
                  ))}
                  {hasMoreConfigs && (
                    <button
                      type="button"
                      className="btn rounded-0 btn-primary text-xs p-1 mt-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePresetExpansion(preset.id);
                      }}
                    >
                      {isExpanded
                        ? `${t('SHOW_LESS')} (${configs.length - INITIAL_VISIBLE_CONFIGS})`
                        : `${t('SHOW_MORE')} (${configs.length - INITIAL_VISIBLE_CONFIGS})`}
                    </button>
                  )}
                </div>
                {preset.subSkillIds && preset.subSkillIds.length > 0 && (
                  <div className="subskills-section mt-2 pt-2 border-top">
                    <span className="text-xs text-muted font-weight-500">{t('SUBSKILLS')}:</span>
                    <div className="subskills-list mt-1">
                      {preset.subSkillIds.map((subskillId, idx) => (
                        <span
                          key={`subskill-${idx}`}
                          className="subskill-tag text-xs px-2 py-1 rounded mr-1 mb-1 d-inline-block"
                        >
                          {subskillId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!selectionMode && (
          <p className="text-sm text-muted mt-3">{t('TESTGROUP_ADVANCED_PRESET_SELECTION_INFO')}</p>
        )}
      </div>
    </Modal>
  );
};

export default AdvancedSettingsModal;
